/**
 * Factoring Service Constants
 * Extended with Compass, TAFS, Engaged Financial (matches Chrome extension provider set).
 */

import {
    CompassAuthStatus,
    EngagedFinancialAuthStatus,
    FactoringProvider,
    FlexentAuthStatus,
    BasicblockAuthStatus,
    GsquaredAuthStatus,
    HaulPayAuthStatus,
    OtrSolutionsAuthStatus,
    RTSAuthStatus,
    StratopayAuthStatus,
    TafsAuthStatus,
    TriumphAuthStatus,
    WexAuthStatus,
    WinfactorAuthStatus,
} from './types'

// Extension ID for communication
export const EXTENSION_ID = 'mfhpdapldnbmejcanhlbbmjlojncicfj'

// Default auth statuses
export const DEFAULT_RTS_STATUS: RTSAuthStatus = {
    isAuthenticated: false,
    hasValidToken: false,
    canRefresh: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_TRIUMPH_STATUS: TriumphAuthStatus = {
    isAuthenticated: false,
    hasValidToken: false,
    canRefresh: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_WEX_STATUS: WexAuthStatus = {
    isAuthenticated: false,
    hasValidCookies: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_WINFACTOR_STATUS: WinfactorAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_GSQUARED_STATUS: GsquaredAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_COMPASS_STATUS: CompassAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_TAFS_STATUS: TafsAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_ENGAGED_FINANCIAL_STATUS: EngagedFinancialAuthStatus = {
    isAuthenticated: true,
    status: 'authenticated',
    needsReauth: false,
    message: 'Public API — no authentication required',
}

export const DEFAULT_HAUL_PAY_STATUS: HaulPayAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_STRATOPAY_STATUS: StratopayAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_OTR_SOLUTIONS_STATUS: OtrSolutionsAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_FLEXENT_STATUS: FlexentAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

export const DEFAULT_BASICBLOCK_STATUS: BasicblockAuthStatus = {
    isAuthenticated: false,
    hasCredentials: false,
    status: 'not_authenticated',
    needsReauth: true,
}

// Provider configurations
const RTS_CONFIG = {
    name: 'RTS Pro',
    loginUrl: 'https://rtspro.com/creditcheck',
    description: 'RTS provides carrier credit ratings and payment history information.',
}

const TRIUMPH_CONFIG = {
    name: 'Triumph',
    loginUrl: 'https://www.mytriumph.com/creditcheck',
    description: 'Triumph provides factoring services and credit status information.',
}

const WEX_CONFIG = {
    name: 'WEX',
    loginUrl: 'https://apps.fleetone.com/FleetDocs/',
    description: 'WEX provides factoring ratings and payment history for carriers.',
}

const WINFACTOR_CONFIG = {
    name: 'PRO Funding',
    loginUrl: '',
    description: 'PRO Funding (WinFactor) provides broker credit ratings and payment verification.',
}

const GSQUARED_CONFIG = {
    name: 'GSquared',
    loginUrl: '',
    description: 'GSquared provides broker credit ratings and payment verification.',
}

const CAPITAL_DEPOT_CONFIG = {
    name: 'Capital Depot',
    loginUrl: '',
    description: 'Capital Depot provides broker credit status and credit score through its WinFactor portal.',
}

const COMPASS_CONFIG = {
    name: 'Compass',
    loginUrl: '',
    description: 'Compass Factoring provides credit ratings, scores, and days-to-pay information.',
}

const TAFS_CONFIG = {
    name: 'TAFS',
    loginUrl: '',
    description: 'TAFS provides credit scores and available credit limits for brokers.',
}

const ENGAGED_FINANCIAL_CONFIG = {
    name: 'Engaged Financial',
    loginUrl: '',
    description: 'Engaged Financial provides broker credit check via public API (no authentication required).',
}

const HAUL_PAY_CONFIG = {
    name: 'HaulPay',
    loginUrl: '',
    description: 'HaulPay provides credit approval, credit limit, and average days-to-pay for brokers.',
}

const STRATOPAY_CONFIG = {
    name: 'Stratopay',
    loginUrl: '',
    description: 'Stratopay (Addio) provides factoring services with invoice submission and broker credit checks.',
}

const OTR_SOLUTIONS_CONFIG = {
    name: 'OTR Solutions',
    loginUrl: '',
    description: 'OTR Solutions provides broker credit checks with status, credit limits, and payment terms.',
}

const OUTGO_CONFIG = {
    name: 'Outgo',
    loginUrl: 'https://app.outgo.com/invoices',
    description: 'Outgo provides factoring services. Sign in to your Outgo account via a new tab.',
}

const ARTISPRO_CONFIG = {
    name: 'Artispro',
    loginUrl: 'https://app.artispro.com',
    description: 'Artispro provides factoring with document-based invoice submission. Sign in via the Numeo extension to connect your account.',
}

const ECAPITAL_CONFIG = {
    name: 'eCapital',
    loginUrl: 'https://portal.ecapital.com/load-management/add-load',
    description: 'eCapital factoring uses the dispatcher browser session through the Chrome extension. Keep the VPN connected before submitting.',
}

const ENGLAND_CONFIG = {
    name: 'England Logistics',
    loginUrl: 'https://englandlogistics.profitstars.com',
    description:
        'England Logistics funds the invoices Artispro submits. Connecting it is READ-ONLY: the extension reads invoice statuses and the broker-collection notes from your own logged-in portal session, and never writes anything back.',
}

const FLEXENT_CONFIG = {
    name: 'Flexent',
    loginUrl: '',
    description:
        'Flexent Freight Funding provides broker credit checks with a verdict, days-to-pay, and an approval expiry date. Sign in with the email OR username you use on the Flexent portal.',
}

const BASICBLOCK_CONFIG = {
    name: 'BasicBlock',
    loginUrl: 'https://carrier.basicblock.io/login',
    description:
        'BasicBlock factors non-recourse, so it keeps an approved-broker list and its Broker Check returns Approved or Rejected for an MC. Sign in with your BasicBlock carrier portal email and password.',
}

export const PROVIDER_CONFIGS = {
    rts: RTS_CONFIG,
    triumph: TRIUMPH_CONFIG,
    triumph_sftp: TRIUMPH_CONFIG,
    wex: WEX_CONFIG,
    winfactor: WINFACTOR_CONFIG,
    gsquared: GSQUARED_CONFIG,
    capital_depot: CAPITAL_DEPOT_CONFIG,
    compass: COMPASS_CONFIG,
    tafs: TAFS_CONFIG,
    engaged_financial: ENGAGED_FINANCIAL_CONFIG,
    haul_pay: HAUL_PAY_CONFIG,
    stratopay: STRATOPAY_CONFIG,
    otr_solutions: OTR_SOLUTIONS_CONFIG,
    outgo: OUTGO_CONFIG,
    artispro: ARTISPRO_CONFIG,
    england: ENGLAND_CONFIG,
    ecapital: ECAPITAL_CONFIG,
    flexent: FLEXENT_CONFIG,
    basicblock: BASICBLOCK_CONFIG,
} as const

// Short display labels for dropdowns (matches extension FACTORING_OPTIONS)
export const PROVIDER_SHORT_LABELS: Record<FactoringProvider, string> = {
    rts: 'RTS',
    triumph: 'Triumph',
    triumph_sftp: 'Triumph SFTP',
    wex: 'WEX',
    winfactor: 'PRO Funding',
    gsquared: 'GSquared',
    capital_depot: 'Capital Depot',
    compass: 'Compass',
    tafs: 'TAFS',
    engaged_financial: 'Engaged',
    haul_pay: 'HaulPay',
    stratopay: 'Stratopay',
    otr_solutions: 'OTR',
    outgo: 'Outgo',
    artispro: 'Artispro',
    england: 'England',
    ecapital: 'eCapital',
    flexent: 'Flexent',
    basicblock: 'BasicBlock',
}

export const PROVIDER_FULL_LABELS: Record<FactoringProvider, string> = {
    rts: 'RTS Financial',
    triumph: 'Triumph Pay',
    triumph_sftp: 'Triumph (SFTP submission)',
    wex: 'WEX Capital',
    winfactor: 'PRO Funding (WinFactor)',
    gsquared: 'GSquared',
    capital_depot: 'Capital Depot',
    compass: 'Compass Factoring',
    tafs: 'TAFS',
    engaged_financial: 'Engaged Financial',
    haul_pay: 'HaulPay',
    stratopay: 'Stratopay',
    otr_solutions: 'OTR Solutions',
    outgo: 'Outgo',
    artispro: 'Artispro',
    england: 'England Logistics',
    ecapital: 'eCapital',
    flexent: 'Flexent Freight Funding',
    basicblock: 'BasicBlock',
}

// Auth login message types (for extension tab-open flow; credential-based providers are empty)
export const AUTH_LOGIN_MESSAGE_TYPES: Record<FactoringProvider, string> = {
    rts: 'OPEN_RTS_LOGIN',
    triumph: 'OPEN_TRIUMPH_LOGIN',
    triumph_sftp: '', // credentials-only — no extension tab flow
    wex: 'OPEN_WEX_LOGIN',
    winfactor: '',
    gsquared: '',
    capital_depot: '',
    compass: '',
    tafs: '',
    engaged_financial: '',
    haul_pay: '',
    stratopay: '',
    otr_solutions: '',
    outgo: 'OPEN_OUTGO_LOGIN',
    artispro: 'OPEN_ARTISPRO_LOGIN',
    england: 'OPEN_ENGLAND_PORTAL',
    ecapital: 'OPEN_ECAPITAL_PORTAL',
    flexent: '', // credentials-only — no extension tab flow
    basicblock: '', // credentials-only — no extension tab flow
}

// Providers that require username/password credentials (admin-managed dialog)
export const CREDENTIAL_BASED_PROVIDERS: FactoringProvider[] = [
    FactoringProvider.WINFACTOR,
    FactoringProvider.GSQUARED,
    FactoringProvider.CAPITAL_DEPOT,
    FactoringProvider.COMPASS,
    FactoringProvider.TAFS,
    FactoringProvider.HAUL_PAY,
    FactoringProvider.STRATOPAY,
    FactoringProvider.OTR_SOLUTIONS,
    FactoringProvider.FLEXENT,
    FactoringProvider.BASICBLOCK,
]

// Providers that need browser tab login (extension flow)

// Providers that don't need any auth (public APIs)
export const NO_AUTH_PROVIDERS: FactoringProvider[] = [FactoringProvider.ENGAGED_FINANCIAL]

/**
 * The single ordering of factoring providers, most preferred first.
 *
 * Two different questions read it, which is why it used to be written out twice:
 *  - which CONNECTED provider's account to show when a project has no `activeFactoringProvider`
 *    stored — that walks the whole list;
 *  - which provider `resolveActiveProvider` should infer from configured tokens and feature flags —
 *    that walks the subset below, because the others cannot be inferred that way.
 * Adding a provider is one entry here, plus the set below if it is inferable.
 */
export const FACTORING_PROVIDER_PRIORITY: readonly FactoringProvider[] = [
    FactoringProvider.TRIUMPH,
    FactoringProvider.RTS,
    FactoringProvider.WINFACTOR,
    FactoringProvider.STRATOPAY,
    FactoringProvider.WEX,
    FactoringProvider.OUTGO,
    FactoringProvider.ARTISPRO,
    FactoringProvider.OTR_SOLUTIONS,
    FactoringProvider.TAFS,
    FactoringProvider.BASICBLOCK,
    FactoringProvider.ECAPITAL,
]

/** Providers `resolveActiveProvider` can infer from configured tokens / feature flags. */
const FLAG_RESOLVABLE_PROVIDERS = new Set<FactoringProvider>([
    FactoringProvider.TRIUMPH,
    FactoringProvider.RTS,
    FactoringProvider.WINFACTOR,
    FactoringProvider.STRATOPAY,
    FactoringProvider.WEX,
    FactoringProvider.TAFS,
    FactoringProvider.ECAPITAL,
])

/**
 * Resolve active factoring provider from configured/connected state and feature flags.
 * Priority: configured providers first (previously connected), then feature flags.
 */
export function resolveActiveProvider(state: {
    configured: Partial<Record<FactoringProvider, boolean>>
    featureFlags: Partial<Record<FactoringProvider, boolean>>
}): FactoringProvider | null {
    const priority = FACTORING_PROVIDER_PRIORITY.filter((provider) => FLAG_RESOLVABLE_PROVIDERS.has(provider))

    // Prefer configured (previously connected) providers
    for (const provider of priority) {
        if (state.configured[provider]) return provider
    }

    // Fallback to feature flags
    for (const provider of priority) {
        if (state.featureFlags[provider]) return provider
    }

    return null
}

/**
 * Whether the project can submit invoices to a factor from the Invoices page.
 *
 * Readiness is driven by the connection status from `/factoring/status`, NOT by a
 * per-provider feature flag. Stratopay, WEX, Outgo and Artispro are ready on
 * connection alone. RTS/ProFunding still carry their legacy feature-flag gate here;
 * their flags are managed elsewhere and are not silently wiped like the factoring
 * flags the Gluon editor hides. Triumph readiness is pre-computed as `triumphCanSubmit`
 * (OAuth flag+connection OR SFTP connection).
 */
export function computeFactoringConnected(state: {
    rtsFeatureEnabled: boolean
    rtsConnected: boolean
    proFundingEnabled: boolean
    winfactorConnected: boolean
    triumphCanSubmit: boolean
    stratopayConnected: boolean
    wexConnected: boolean
    outgoConnected: boolean
    artisproConnected: boolean
    otrSolutionsConnected: boolean
    tafsConnected: boolean
    basicblockConnected: boolean
    ecapitalConnected: boolean
}): boolean {
    return (
        (state.rtsFeatureEnabled && state.rtsConnected) ||
        (state.proFundingEnabled && state.winfactorConnected) ||
        state.triumphCanSubmit ||
        state.stratopayConnected ||
        state.wexConnected ||
        state.outgoConnected ||
        state.artisproConnected ||
        state.otrSolutionsConnected ||
        state.tafsConnected ||
        state.basicblockConnected ||
        state.ecapitalConnected
    )
}
